<template>
  <div class="patientflow-page">
    <div class="pf-top">
      <div class="container pf-top-inner">
        <img :src="logo" alt="PatientFlow, Medical Appointment Partner Program" class="pf-logo" />
        <router-link to="/" class="pf-back">Volver a Hispano Medical Plan</router-link>
      </div>
    </div>

    <section class="pf-hero">
      <div class="container">
        <div class="pf-hero-grid">
          <div>
            <span class="pf-badge">Programa aparte del plan de membresía</span>
            <h1>Paga por citas gestionadas y asistidas</h1>
            <p>
              Cada cita que generas y a la que tu paciente asiste se acredita
              directamente en tu cuenta de agente.
            </p>
          </div>
          <div class="pf-amount">
            <div class="pf-amount-n">$25</div>
            <p>
              por cada cita asistida. No aplica a citas de follow-up ni de
              resultados de laboratorio.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="pf-section">
      <div class="container">
        <h2 class="pf-section-title">Qué citas aplican</h2>
        <div class="pf-rules">
          <div class="pf-rule yes">
            <span class="tag">Aplica</span>
            <h3>Cita asistida</h3>
            <p>El paciente debe asistir a la cita para que se genere el incentivo.</p>
          </div>
          <div class="pf-rule no">
            <span class="tag">No aplica</span>
            <h3>Citas de follow-up</h3>
            <p>Las citas de seguimiento no generan incentivo.</p>
          </div>
          <div class="pf-rule no">
            <span class="tag">No aplica</span>
            <h3>Resultados de laboratorio</h3>
            <p>Las citas para entregar resultados de laboratorio no generan incentivo.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="pf-section">
      <div class="container pf-how">
        <div>
          <h2 class="pf-section-title">Asignación y principio operativo</h2>
          <p class="pf-lede">
            $25 por cita asistida, acreditados directamente en la cuenta del
            agente que generó la cita.
          </p>
        </div>
        <div>
          <h3 class="pf-how-subtitle">Este esquema asegura</h3>
          <ul class="pf-how-list">
            <li>Pago basado en la asistencia real del paciente</li>
            <li>Control de calidad sobre la generación de citas</li>
            <li>Alineación entre la demanda generada y el uso real del servicio</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="pf-section">
      <div class="container">
        <h2 class="pf-section-title">Calcula lo que genera tu cartera</h2>
        <div class="pf-calc">
          <div>
            <label for="pf-people">Personas en tu cartera</label>
            <div class="pf-val">{{ people }}</div>
            <input
              id="pf-people"
              v-model.number="people"
              type="range"
              min="10"
              max="500"
              step="10"
            />
            <label for="pf-citas">Citas asistidas por persona al mes</label>
            <div class="pf-val">{{ citasLabel }}</div>
            <input
              id="pf-citas"
              v-model.number="citasPerPerson"
              type="range"
              min="0.25"
              max="2"
              step="0.25"
            />
            <p class="pf-note">
              Solo cuentan citas que califican. Ejemplo ilustrativo, no garantía
              de ingresos.
            </p>
          </div>
          <div class="pf-out">
            <div>
              <span>Citas al mes</span>
              <b>{{ citasMesFormatted }}</b>
            </div>
            <div>
              <span>Ganas al mes</span>
              <b>{{ mesFormatted }}</b>
            </div>
            <div class="yr">
              <span>Proyección anual</span>
              <b>{{ anoFormatted }}</b>
            </div>
          </div>
        </div>
        <div class="pf-cta text-center">
          <a
            href="/providers#form-section"
            class="btn btn-pf-register"
            @click.prevent="goToProvidersForm"
          >
            Registrarse como agente <i class="fas fa-chevron-right ml-2"></i>
          </a>
        </div>
      </div>
    </section>

    <section class="pf-footer-band">
      <div class="container">
        <div class="pf-footer-row">
          <img :src="logo" alt="PatientFlow" class="pf-logo-sm" />
          <router-link to="/" class="pf-back pf-back-light">
            <img :src="hmpLogoWhite" alt="" class="pf-hmp-mini" />
            Volver a Hispano Medical Plan
          </router-link>
        </div>
        <p class="pf-disc">
          PatientFlow es un programa de incentivos por citas, independiente de la
          membresía Hispano Medical Plan. Los montos se acreditan solo por citas
          asistidas que califican.
        </p>
        <p class="pf-disc pf-disc-hmp">{{ hmpDisclaimer }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import scrollToProvidersForm from '../mixins/scrollToProvidersForm'
import { HMP_LEGAL_DISCLAIMER } from '../data/hmpLinks2027'

/**
 * Landing PatientFlow: programa de incentivos por citas asistidas ($25).
 * Vista aparte del plan de membresía (reemplaza CareFlow).
 */
export default {
  name: 'PatientFlow',
  mixins: [scrollToProvidersForm],
  data() {
    return {
      logo: require('@/assets/lp/patientflow/patientflow-logo.png'),
      hmpLogoWhite: require('@/assets/lp/hmp/hmp-logo-blanco.png'),
      hmpDisclaimer: HMP_LEGAL_DISCLAIMER,
      people: 100,
      citasPerPerson: 1,
      incentive: 25
    }
  },
  computed: {
    citasMes() {
      return this.people * this.citasPerPerson
    },
    citasLabel() {
      return String(this.citasPerPerson).replace('.', ',')
    },
    citasMesFormatted() {
      return Math.round(this.citasMes).toLocaleString('en-US')
    },
    mesFormatted() {
      return this.formatMoney(this.citasMes * this.incentive)
    },
    anoFormatted() {
      return this.formatMoney(this.citasMes * this.incentive * 12)
    }
  },
  methods: {
    formatMoney(n) {
      return `$${Math.round(n).toLocaleString('en-US')}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.patientflow-page {
  background: $pf-bg;
  min-height: 60vh;
  color: $landing-text-dark;
}

.pf-top {
  background: #fff;
  border-bottom: 1px solid #dce6ef;

  .pf-top-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 72px;
    gap: 1rem;
    flex-wrap: wrap;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .pf-logo {
    height: 46px;
    width: auto;
  }
}

.pf-back {
  background: none;
  border: 1.5px solid $pf-navy;
  color: $pf-navy;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.95rem;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: $pf-navy;
    color: #fff;
    text-decoration: none;
  }

  &.pf-back-light {
    border-color: #fff;
    color: #fff;

    &:hover {
      background: #fff;
      color: $pf-navy;
    }
  }
}

.pf-hero {
  padding: 72px 0 56px;

  .pf-hero-grid {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 48px;
    align-items: center;
  }

  h1 {
    font-size: clamp(2.5rem, 6vw, 4.75rem);
    font-weight: 800;
    color: $pf-navy;
    line-height: 0.96;
    margin: 0;
  }

  p {
    font-size: 1.2rem;
    color: $landing-text-dark;
    font-weight: 500;
    line-height: 1.55;
    margin-top: 20px;
    max-width: 56ch;
  }
}

.pf-badge {
  display: inline-block;
  margin-bottom: 18px;
  color: $pf-green;
  font-weight: 700;
}

.pf-amount {
  background: $pf-navy;
  color: #fff;
  border-radius: 28px;
  padding: 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    right: -60px;
    bottom: -60px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: linear-gradient(135deg, #00a3db, #3dbe6e);
    opacity: 0.9;
  }

  .pf-amount-n {
    font-weight: 900;
    font-size: clamp(5rem, 12vw, 9.5rem);
    line-height: 0.85;
    position: relative;
  }

  p {
    position: relative;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.45;
    color: #ffffff;
    margin-top: 12px;
    margin-bottom: 0;
    max-width: 32ch;
  }
}

.pf-section {
  padding: 24px 0 48px;
}

.pf-section-title {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 800;
  color: $pf-navy;
  margin-bottom: 1.5rem;
}

.pf-lede {
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.55;
  color: $landing-text-dark;
  margin-top: 16px;
  max-width: 42ch;
}

.pf-rules {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 16px;
}

.pf-rule {
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 20px;
  padding: 28px;

  .tag {
    font-weight: 700;
    font-size: 0.875rem;
    margin-bottom: 10px;
    display: block;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.35rem;
    color: inherit;
  }

  p {
    margin: 8px 0 0;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.5;
  }

  &.yes {
    background: $pf-green;
    color: #fff;
    border-color: $pf-green;

    p {
      color: #ffffff;
      font-weight: 600;
      opacity: 1;
    }
  }

  &.no {
    .tag {
      color: #b4561a;
    }

    h3 {
      color: $landing-text-dark;
    }

    p {
      color: $landing-text-dark;
      font-weight: 500;
      opacity: 1;
    }
  }
}

.pf-how {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

.pf-how-subtitle {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 6px;
  color: $pf-navy;
}

.pf-how-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    padding: 16px 0;
    border-bottom: 1px solid $landing-border-light;
    font-size: 1.1rem;
  }
}

.pf-calc {
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 24px;
  padding: 32px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  label {
    display: block;
    font-weight: 700;
    margin-bottom: 6px;
    margin-top: 12px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  input[type='range'] {
    width: 100%;
    accent-color: $pf-blue;
    margin: 8px 0 12px;
  }
}

.pf-val {
  font-weight: 800;
  font-size: 2.5rem;
  line-height: 1;
  color: $pf-blue;
}

.pf-note {
  font-size: 0.8rem;
  color: $landing-text-medium;
  margin-top: 8px;
  margin-bottom: 0;
}

.pf-out {
  display: grid;
  gap: 16px;
  align-content: center;

  > div {
    border-top: 2px solid $landing-text-dark;
    padding-top: 10px;
  }

  span {
    display: block;
    color: $landing-text-medium;
    font-size: 0.875rem;
  }

  b {
    font-weight: 900;
    font-size: clamp(2rem, 4vw, 3.5rem);
    line-height: 1;
    color: $pf-navy;
  }

  .yr {
    background: $pf-navy;
    border-radius: 18px;
    padding: 20px;
    border: 0;

    span {
      color: #cfe0f2;
    }

    b {
      color: #fff;
    }
  }
}

.pf-cta {
  margin-top: 2rem;
}

.btn-pf-register {
  display: inline-flex;
  align-items: center;
  background: $pf-navy;
  color: #fff;
  border-radius: 10px;
  padding: 14px 28px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: darken($pf-navy, 6%);
    color: #fff;
    text-decoration: none;
    transform: translateY(-1px);
  }
}

.pf-footer-band {
  // Mismo navy que LandingFooter para que no se vean dos azules distintos
  background: $landing-primary-color;
  color: #b9cad6;
  padding: 40px 0;

  .pf-footer-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }

  .pf-logo-sm {
    height: 40px;
    background: #fff;
    border-radius: 8px;
    padding: 4px 8px;
  }

  .pf-disc {
    font-size: 0.875rem;
    margin: 0;
    max-width: 70ch;
  }

  .pf-disc-hmp {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    max-width: 95ch;
  }

  .pf-hmp-mini {
    height: 22px;
    width: auto;
  }

  .pf-back-light {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
}

@media (max-width: 860px) {
  .pf-hero .pf-hero-grid,
  .pf-how,
  .pf-calc {
    grid-template-columns: 1fr;
  }

  .pf-rules {
    grid-template-columns: 1fr;
  }

  .pf-hero {
    padding: 40px 0 32px;
  }
}
</style>
